import { ITableViewCell, ITableViewColumn, IUpdateTableViewCell } from 'types'
import { ISpreadsheetData } from 'components/spreadsheet/types'
import { IMatrixComputeResult, mapMatrixCells } from 'components/spreadsheet/helpers/matrix'
import { transformStringToCorrectFormat } from 'components/spreadsheet/helpers/paste'
import { ViewAdapterResult } from './types'

const ARRAY_KINDS = new Set(['vote', 'attachment', 'multilink', 'multiselect'])

/** Adapter for Matrix views.
 *
 * Data cells are a 1-1 mapping back to the original source row; edits flow
 * through ``mapMatrixCells`` exactly like the transpose adapter. Row-label
 * synthetic columns and conflict / sparse cells stay read-only because they
 * have no unambiguous source target. */
export function createMatrixAdapter(
  spreadsheetData: ISpreadsheetData,
  matrixResult: IMatrixComputeResult
): ViewAdapterResult {
  const coordinateMap = matrixResult.coordinateMap

  return {
    effectiveRows: matrixResult.rows,
    effectiveTotalRows: matrixResult.rows.length,
    effectiveColumnIdToIndex: matrixResult.columnIdToIndex,
    effectiveIndexToColumnId: matrixResult.indexToColumnId,
    effectiveColumns: matrixResult.columns,

    rowsViewportOverrides: {
      groupBy: [],
      disableNewRow: true,
      unpackMultiselectGroupView: false,
      treeGroupView: false,
      columnStore: null,
      visibleBitmap: null,
      visibleCount: matrixResult.rows.length
    },

    columnsViewportOverrides: {
      frozenIndex: spreadsheetData.viewDetails.frozenIndex,
      rawGroupByColumns: [],
      hiddenColumnIds: [],
      treeGroupView: false
    },

    effectiveSpreadsheetData: {
      ...spreadsheetData,
      rows: matrixResult.rows,
      viewDetails: {
        ...spreadsheetData.viewDetails,
        columns: matrixResult.columns
      },
      columnIdToIndex: matrixResult.columnIdToIndex,
      indexToColumnId: matrixResult.indexToColumnId,
      viewContext: {
        originalColumns: spreadsheetData.viewDetails.columns,
        originalRows: spreadsheetData.rows,
        originalColumnIdToIndex: spreadsheetData.columnIdToIndex,
        originalColumnStore: spreadsheetData.columnStore,
        coordinateMap,
        truncations: matrixResult.truncations,
        matrixCollisionCells: matrixResult.collisionCells,
        matrixSparseCells: matrixResult.sparseCells,
        readOnlyColumns: matrixResult.readOnlyColumns
      },
      columnStore: null,
      totalRows: matrixResult.rows.length,
      totalRowsOnServer: matrixResult.rows.length,
      visibleBitmap: null,
      visibleCount: matrixResult.rows.length,
      rowStreamComplete: true
    },

    mapCellsForWrite: (cells: IUpdateTableViewCell) => mapMatrixCells(cells, coordinateMap),

    transformPastedCells: (cells: ITableViewCell[], viewColumns: ITableViewColumn[]) => {
      for (const cell of cells) {
        const originalCol = viewColumns.find((c) => c.publicId === cell.columnId)
        if (originalCol && ARRAY_KINDS.has(originalCol.kind)) {
          cell.value = transformStringToCorrectFormat(cell.value as string | null, originalCol.kind)
        }
      }
    },

    columnStoreForCopy: null,

    getRenderedRows: () => matrixResult.rows,

    rowsForSelectionCheck: matrixResult.rows,
    bitmapForSelectionCheck: null,

    coordinateMap
  }
}
